import React, { useState, useEffect } from "react";
import { C, fmt, text } from "../theme";

import { Card, Btn, Notice, ConfirmDialog } from "../components/ui";

const Toggle = ({ on, onClick }) => (
  <div onClick={onClick} style={{ width: 40, height: 22, borderRadius: 11, background: on ? C.success : C.gray300, padding: 2, cursor: "pointer", display: "inline-block" }}>
    <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.white, marginLeft: on ? 18 : 0, transition: "margin 0.2s" }} />
  </div>
);

export default function SettingsPage() {
  const [tab, setTab] = useState("checklist");
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add checklist form
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [cForm, setCForm] = useState({ name: "", condition: "Always", conditionAmt: 0, appliesToAOR: true, appliesToPO: true, mandatory: true });
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  // Xero connection
  const [xero, setXero] = useState({ connected: false, tenantName: null, stale: false });
  const loadXero = () => {
    fetch("/api/xero/status").then(r => r.json()).then(setXero).catch(console.error);
  };
  useEffect(loadXero, []);

  const handleXeroConnect = async () => {
    try {
      const { url } = await (await fetch("/api/xero/connect")).json();
      window.location.href = url;
    } catch (e) { setNotice({ kind: "error", text: "Could not start Xero connection" }); }
  };
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const handleXeroDisconnect = async () => {
    await fetch("/api/xero/disconnect", { method: "POST" });
    setNotice({ kind: "success", text: "Disconnected from Xero." });
    loadXero();
  };

  const [syncing, setSyncing] = useState(false);
  const handleXeroSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await fetch("/api/xero/sync", { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setNotice({ kind: "error", text: data.error || "Sync failed" }); }
      loadXero();
    } catch (e) { setNotice({ kind: "error", text: "Sync failed" }); }
    finally { setSyncing(false); }
  };

  const load = () => {
    setLoading(true);
    fetch("/api/settings/checklist").then(r => r.json())
      .then((c) => { setChecklist(c); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleAddChecklist = async () => {
    if (!cForm.name) { setNotice({ kind: "error", text: "Name is required" }); return; }
    const res = await fetch("/api/settings/checklist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cForm),
    });
    // On failure the add panel stays open with its input intact.
    if (!res.ok) { setNotice({ kind: "error", text: "Error: " + (await res.json()).error }); return; }
    setShowAddChecklist(false);
    setCForm({ name: "", condition: "Always", conditionAmt: 0, appliesToAOR: true, appliesToPO: true, mandatory: true });
    load();
  };

  const handleToggleChecklist = async (id, field, value) => {
    await fetch("/api/settings/checklist/" + id, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: !value }),
    });
    load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  const tabs = [
    { id: "checklist", label: "Document Checklist" },
    { id: "xero", label: "Xero Connection" },
    { id: "notifications", label: "Notifications" },
  ];

  return (
    <div>
      <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: "0 0 16px" }}>Settings</h1>

      <div style={{ fontSize: text.md, color: C.gray600, marginBottom: 16 }}>Approval routing is now configured in <strong>Admin → Approval Workflows</strong>.</div>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `2px solid ${C.gray200}` }}>
        {tabs.map(t => (
          <div key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 18px", fontSize: text.md, fontWeight: tab === t.id ? 700 : 500,
            color: tab === t.id ? C.primary : C.gray600, cursor: "pointer",
            borderBottom: tab === t.id ? `3px solid ${C.primary}` : "3px solid transparent", marginBottom: -2,
          }}>{t.label}</div>
        ))}
      </div>

      {/* ─── Checklist Tab ─── */}
      {tab === "checklist" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: text.sm, color: C.gray500 }}>Configure mandatory documents for AOR and PO submissions.</div>
            <Btn onClick={() => setShowAddChecklist(!showAddChecklist)}>+ Add Document</Btn>
          </div>

          {showAddChecklist && (
            <Card style={{ marginBottom: 16, border: `2px solid ${C.primary}30` }}>
              <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 12 }}>New Checklist Item</div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label htmlFor="set-doc-name" style={{ fontSize: text.xs, fontWeight: 600, color: C.gray700 }}>Document Name *</label>
                  <input id="set-doc-name" value={cForm.name} onChange={e => setCForm({ ...cForm, name: e.target.value })} placeholder="e.g. Technical Specifications" style={{ width: "100%", padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, marginTop: 4, fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label htmlFor="set-condition" style={{ fontSize: text.xs, fontWeight: 600, color: C.gray700 }}>Condition</label>
                  <input id="set-condition" value={cForm.condition} onChange={e => setCForm({ ...cForm, condition: e.target.value })} placeholder="e.g. Value ≥ $3,000" style={{ width: "100%", padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, marginTop: 4, fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label htmlFor="set-condition-amt" style={{ fontSize: text.xs, fontWeight: 600, color: C.gray700 }}>Condition Amount ($)</label>
                  <input id="set-condition-amt" type="number" value={cForm.conditionAmt} onChange={e => setCForm({ ...cForm, conditionAmt: parseFloat(e.target.value) || 0 })} style={{ width: "100%", padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, marginTop: 4, fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 20, marginBottom: 12, fontSize: text.md }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={cForm.appliesToAOR} onChange={e => setCForm({ ...cForm, appliesToAOR: e.target.checked })} /> Applies to AOR</label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={cForm.appliesToPO} onChange={e => setCForm({ ...cForm, appliesToPO: e.target.checked })} /> Applies to PO</label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={cForm.mandatory} onChange={e => setCForm({ ...cForm, mandatory: e.target.checked })} /> Mandatory</label>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn onClick={handleAddChecklist}>Save</Btn>
                <Btn variant="outline" onClick={() => setShowAddChecklist(false)}>Cancel</Btn>
              </div>
            </Card>
          )}

          <Card>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
              <thead><tr>{["Document Name", "Condition", "AOR", "PO", "Mandatory", "Active"].map((h, i) => (
                <th scope="col" key={i} style={{ padding: "8px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {checklist.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${C.gray100}`, opacity: c.isActive ? 1 : 0.5 }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{c.name}</td>
                    <td style={{ padding: "10px 12px", color: C.gray600 }}>{c.condition}{c.conditionAmt > 0 ? ` (≥${fmt(c.conditionAmt)})` : ""}</td>
                    <td style={{ padding: "10px 12px" }}><Toggle on={c.appliesToAOR} onClick={() => handleToggleChecklist(c.id, "appliesToAOR", c.appliesToAOR)} /></td>
                    <td style={{ padding: "10px 12px" }}><Toggle on={c.appliesToPO} onClick={() => handleToggleChecklist(c.id, "appliesToPO", c.appliesToPO)} /></td>
                    <td style={{ padding: "10px 12px" }}><Toggle on={c.mandatory} onClick={() => handleToggleChecklist(c.id, "mandatory", c.mandatory)} /></td>
                    <td style={{ padding: "10px 12px" }}><Toggle on={c.isActive} onClick={() => handleToggleChecklist(c.id, "isActive", c.isActive)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ─── Xero Tab ─── */}
      {tab === "xero" && (
        <Card>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Xero API Connection</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: xero.connected ? C.successBg : C.warningBg, borderRadius: 8, border: `1px solid ${xero.connected ? C.success : C.warningLight}`, marginBottom: 16 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: xero.connected ? C.success : C.warning }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: text.md, fontWeight: 600 }}>
                {xero.connected ? `Connected — ${xero.tenantName}` : "Not Connected"}
                {xero.stale ? " (reconnect required)" : ""}
              </div>
              <div style={{ fontSize: text.sm, color: C.gray600 }}>
                {xero.connected ? "Approved invoices push to this Xero organisation." : "Connect to push approved invoices to Xero."}
              </div>
            </div>
            {xero.connected
              ? <Btn variant="dangerOutline" onClick={() => setConfirmDisconnect(true)}>Disconnect</Btn>
              : <Btn onClick={handleXeroConnect}>Connect to Xero</Btn>}
          </div>
          <ConfirmDialog
            open={confirmDisconnect}
            title={`Disconnect from ${xero.tenantName || "Xero"}?`}
            confirmLabel="Disconnect"
            variant="danger"
            onCancel={() => setConfirmDisconnect(false)}
            onConfirm={() => { setConfirmDisconnect(false); handleXeroDisconnect(); }}
          >
            Pushing invoices and creating payment runs stop working until someone reconnects. Already-pushed invoices keep their Xero links.
          </ConfirmDialog>
          {xero.connected && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: text.md, fontWeight: 600 }}>Data sync</div>
                <Btn onClick={handleXeroSyncNow} disabled={syncing}>{syncing ? "Syncing…" : "Sync now"}</Btn>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.sm }}>
                <thead><tr>{["Entity", "Last sync", "Status"].map((h, i) => (
                  <th scope="col" key={i} style={{ padding: "6px 10px", textAlign: "left", borderBottom: `1px solid ${C.gray200}`, color: C.gray600, fontWeight: 600 }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {(xero.sync || []).length === 0 && (
                    <tr><td colSpan={3} style={{ padding: "8px 10px", color: C.gray500 }}>No sync has run yet.</td></tr>
                  )}
                  {(xero.sync || []).map(s => (
                    <tr key={s.entity} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                      <td style={{ padding: "6px 10px" }}>{s.entity}</td>
                      <td style={{ padding: "6px 10px", color: C.gray600 }}>{s.lastSyncAt ? new Date(s.lastSyncAt).toLocaleString() : "—"}</td>
                      <td style={{ padding: "6px 10px", color: s.lastStatus === "ERROR" ? C.danger : C.success }}>{s.lastStatus || "—"}{s.lastError ? ` — ${s.lastError}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize: text.sm, color: C.gray600, lineHeight: 1.6 }}>
            When connected, the system will:
          </div>
          <div style={{ marginTop: 8, fontSize: text.sm, color: C.gray700, lineHeight: 1.8 }}>
            • Push approved invoices as Bills to Xero Accounts Payable<br/>
            • Sync vendor contacts from Xero<br/>
            • Map account codes to Xero Chart of Accounts<br/>
            • Pull payment status updates back from Xero
          </div>
        </Card>
      )}

      {/* ─── Notifications Tab ─── */}
      {tab === "notifications" && (
        <Card>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 14 }}>Notification Settings</div>
          <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 16 }}>Configure email and Teams notifications via Microsoft Graph API.</div>
          {[
            { label: "Email notifications for approval requests", on: true },
            { label: "Teams notifications for approval requests", on: true },
            { label: "Email on Xero push success/failure", on: true },
            { label: "Email on budget virement execution", on: true },
            { label: "Daily digest of pending actions", on: false },
            { label: "Weekly budget utilisation report", on: false },
          ].map((n, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.gray100}` }}>
              <span style={{ fontSize: text.md }}>{n.label}</span>
              <Toggle on={n.on} onClick={() => {}} />
            </div>
          ))}
          <div style={{ marginTop: 16, padding: 14, background: C.warningBg, borderRadius: 8, fontSize: text.sm, color: C.warning }}>
            ⚠ Email and Teams notifications require Microsoft Graph API connection (pending Entra ID setup).
          </div>
        </Card>
      )}
    </div>
  );
}